/**
 * Display formatting for the redesign (§2.7): one date format everywhere
 * (`Jan 7, 2026`), money always formatted (the audit's raw `429200` cells),
 * and renewal urgency mapped to badge variants (amber ≤30d, red ≤7d).
 */

// Policy dates are calendar dates (effective/expiration), not instants —
// format in UTC so "2026-08-12" never renders as Aug 11 in US timezones.
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC"
});

const MONEY_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0
});

export function formatDate(value) {
  if (!value) {
    return "—";
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : DATE_FORMAT.format(date);
}

export function formatMoney(value) {
  const n = typeof value === "string" ? Number(value.replace(/[$,\s]/g, "")) : value;
  if (n == null || Number.isNaN(n)) {
    return value ? String(value) : "—"; // non-numeric coverage strings display as-is
  }
  return MONEY_FORMAT.format(n);
}

export function daysUntil(value) {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

/** Badge variant for a renewal N days out (§2.8 renewals-as-a-feed). */
export function renewalVariant(days) {
  if (days == null) {
    return "neutral";
  }
  if (days <= 7) {
    return "danger";
  }
  // E QA L-2: amber inside 14 days only — at 30/60 it's wallpaper, not signal.
  // (The radar's row-inclusion WINDOW is separate and stays at 60/90 days.)
  if (days <= 14) {
    return "warning";
  }
  return "neutral";
}

/**
 * A policy's renewal date. Imported (export-v2) policies carry a real
 * `expirationDate`; legacy app-created policies usually only have
 * `effectiveDate`. For those we roll the effective date forward to its next
 * anniversary and mark the result estimated — surfaced as "est." in the UI,
 * never silently presented as fact. (Flagged to the Coordinator: a real
 * renewal field on legacy policies would remove the estimate.)
 */
export function renewalInfo(policy) {
  if (policy.expirationDate) {
    const date = new Date(policy.expirationDate);
    if (!Number.isNaN(date.getTime())) {
      return { date, estimated: false };
    }
  }
  if (policy.effectiveDate) {
    const date = new Date(policy.effectiveDate);
    if (!Number.isNaN(date.getTime())) {
      const anniversary = new Date(date);
      anniversary.setFullYear(new Date().getFullYear());
      if (anniversary.getTime() < Date.now()) {
        anniversary.setFullYear(anniversary.getFullYear() + 1);
      }
      return { date: anniversary, estimated: true };
    }
  }
  return null;
}

/** The legacy dashboard's estimated-commission formula (premium × 2.5%). */
export const COMMISSION_RATE = 0.025;

/**
 * Premium parsing + aggregate sanity (P1 from Evan's gate smoke, 2026-07-12).
 *
 * The live book carries at least one garbage premium (a home policy holding
 * a phone-number-shaped 1,593,650,660), which inflated Today's staff stats
 * by three orders of magnitude. Aggregates must be robust to data like
 * that WITHOUT lying: implausible values are excluded from sums and the
 * exclusion is surfaced to the user; detail rows keep showing the raw
 * record so the bad data stays visible and gets fixed at the source.
 */
export const PREMIUM_SANITY_CAP = 250000; // personal-lines annual premium above this is data garbage

export function parsePremium(value) {
  if (value == null) {
    return 0;
  }
  const n = typeof value === "string" ? Number(value.replace(/[$,\s]/g, "")) : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Sum premiums for aggregate stats: {total, excluded} — excluded entries
    are the policies whose premium failed the plausibility check. */
export function sumPremiums(policies) {
  let total = 0;
  const excluded = [];
  for (const policy of policies) {
    const n = parsePremium(policy.premium);
    if (n >= PREMIUM_SANITY_CAP) {
      excluded.push(policy);
    } else {
      total += n;
    }
  }
  return { total, excluded };
}
